import { useEffect, useRef, useState } from "react";
import { Check, Sparkles, ArrowRight, Copy, Play } from "lucide-react";

/**
 * Animated "Pick → Paste → Profit" landing-page demo.
 * Zero video files. Pure DOM. Loops on a 14s timeline.
 *
 * Stage 0 (0.0s → 2.6s):  PICK a track
 * Stage 1 (2.6s → 6.2s):  PASTE a prompt (typing effect)
 * Stage 2 (6.2s → 12.4s): PROFIT (AI streams output)
 * Stage 3 (12.4s → 14s):  Reset fade → loop
 */

const TRACKS = [
  { name: "E-book Writing", tone: "text-purple-300" },
  { name: "Faceless YouTube", tone: "text-zinc-300" },
  { name: "Etsy Digital Products", tone: "text-zinc-300" },
];

const PROMPT_TEXT = `Find 5 profitable e-book niches for a first-time author. Score each by demand, competition & margin. Rank them.`;

const AI_WORDS = [
  "Analysing", "market", "gaps", "for", "solo", "authors", "…",
  "\n\n",
  "1.", "Micro-habits", "for", "ADHD", "adults", "—", "demand", "9/10", "·", "comp", "4/10",
  "\n",
  "2.", "Vegan", "meal-prep", "for", "shift", "workers", "—", "demand", "8/10", "·", "comp", "3/10",
  "\n",
  "3.", "AI", "prompt", "playbooks", "for", "estate", "agents", "—", "demand", "8/10", "·", "comp", "2/10",
  "\n",
  "4.", "Grief", "journals", "for", "widowers", "—", "demand", "7/10", "·", "comp", "3/10",
  "\n",
  "5.", "Freelance", "tax", "guides", "(UK)", "—", "demand", "9/10", "·", "comp", "5/10",
  "\n\n",
  "Top", "pick:", "#3.", "Ship", "in", "72h.",
];

const STAGE_TIMINGS = { pick: 2600, paste: 3600, profit: 6200, rest: 1600 };
const TOTAL = STAGE_TIMINGS.pick + STAGE_TIMINGS.paste + STAGE_TIMINGS.profit + STAGE_TIMINGS.rest;

export function PickPasteProfitDemo() {
  const [stage, setStage] = useState(0); // 0=pick, 1=paste, 2=profit, 3=rest
  const [typedChars, setTypedChars] = useState(0);
  const [streamedWords, setStreamedWords] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduceMotion) {
      setStage(3);
      setTypedChars(PROMPT_TEXT.length);
      setStreamedWords(AI_WORDS.length);
      return undefined;
    }
    let start = performance.now();
    const tick = (now) => {
      if (document.hidden) return;
      const elapsed = (now - start) % TOTAL;
      // Determine stage
      if (elapsed < STAGE_TIMINGS.pick) {
        setStage(0);
        setTypedChars(0);
        setStreamedWords(0);
      } else if (elapsed < STAGE_TIMINGS.pick + STAGE_TIMINGS.paste) {
        setStage(1);
        const pasteProgress = (elapsed - STAGE_TIMINGS.pick) / STAGE_TIMINGS.paste;
        setTypedChars(Math.floor(pasteProgress * PROMPT_TEXT.length));
        setStreamedWords(0);
      } else if (elapsed < STAGE_TIMINGS.pick + STAGE_TIMINGS.paste + STAGE_TIMINGS.profit) {
        setStage(2);
        setTypedChars(PROMPT_TEXT.length);
        const streamProgress =
          (elapsed - STAGE_TIMINGS.pick - STAGE_TIMINGS.paste) / STAGE_TIMINGS.profit;
        setStreamedWords(Math.floor(streamProgress * AI_WORDS.length));
      } else {
        setStage(3);
        setTypedChars(PROMPT_TEXT.length);
        setStreamedWords(AI_WORDS.length);
      }
    };
    tick(start);
    timerRef.current = window.setInterval(() => tick(performance.now()), 60);
    return () => window.clearInterval(timerRef.current);
  }, []);

  const selectedTrack = TRACKS[0]; // Always E-book Writing for the demo
  const streamedText = AI_WORDS.slice(0, streamedWords).join(" ").replace(/ \n/g, "\n");

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-purple-500/25 bg-[#0a080d] p-1 shadow-[0_24px_70px_-40px_rgba(168,85,247,0.65)]"
      data-testid="landing-demo"
    >
      <div className="relative rounded-md bg-black/60 backdrop-blur-sm">
        {/* Fake browser chrome */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-purple-500/10">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-800" />
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-800" />
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-800" />
          </div>
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-purple-400/70 font-mono">
            stagvault.co.uk / vault
          </div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 hidden sm:block">
            Live
          </div>
        </div>

        {/* Stage labels */}
        <div className="grid grid-cols-3 gap-2 px-4 pt-4 pb-2 text-[10px] md:text-xs tracking-[0.28em] uppercase font-mono">
          {["01 Pick", "02 Paste", "03 Profit"].map((label, i) => {
            const active = stage === i || (stage === 3 && i === 2);
            const done = stage > i && stage !== 3;
            return (
              <div
                key={label}
                className={`flex items-center gap-1.5 transition-[color,opacity] duration-500 ${
                  active
                    ? "text-purple-300"
                    : done
                    ? "text-purple-400/60"
                    : "text-zinc-600"
                }`}
                data-testid={`demo-stage-${i}`}
              >
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full transition-[background-color,box-shadow] duration-500 ${
                    active
                      ? "bg-purple-400 shadow-[0_0_12px_rgba(168,85,247,0.9)]"
                      : done
                      ? "bg-purple-500/50"
                      : "bg-zinc-700"
                  } transition-[background-color,box-shadow]`}
                />
                <span className="truncate">{label}</span>
              </div>
            );
          })}
        </div>

        {/* Main canvas — fixed height so nothing jumps */}
        <div className="px-4 pb-5 pt-2 min-h-[280px] md:min-h-[320px]">
          {/* ── STAGE 0 · PICK ────────────────────────────── */}
          <div
            className={`transition-[opacity,transform] duration-500 ${
              stage === 0 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none absolute inset-x-4"
            }`}
            aria-hidden={stage !== 0}
          >
            <div className="text-zinc-500 text-xs font-mono mb-3">Choose a track</div>
            <div className="flex flex-col gap-2">
              {TRACKS.map((t, i) => (
                <div
                  key={t.name}
                  className={`flex items-center justify-between rounded-md border px-4 py-3 transition-[border-color,background-color,box-shadow,transform] duration-500 ${
                    i === 0 && stage === 0
                      ? "border-purple-400/60 bg-purple-500/10 shadow-[0_0_25px_-5px_rgba(168,85,247,0.5)] scale-[1.01]"
                      : "border-zinc-800 bg-zinc-950/50"
                  }`}
                  style={{
                    animation: i === 0 && stage === 0 ? "demoPulse 2.6s ease-in-out" : undefined,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-mono ${
                        i === 0 ? "bg-purple-500/20 text-purple-300" : "bg-zinc-800 text-zinc-500"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`text-sm font-medium ${i === 0 ? "text-purple-200" : "text-zinc-500"}`}>
                      {t.name}
                    </span>
                  </div>
                  {i === 0 && stage === 0 && (
                    <span className="text-[10px] tracking-[0.25em] uppercase text-purple-400 font-mono">
                      ← click
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── STAGE 1 · PASTE ────────────────────────────── */}
          <div
            className={`transition-[opacity,transform] duration-500 ${
              stage === 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none absolute inset-x-4"
            }`}
            aria-hidden={stage !== 1}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-zinc-500 text-xs font-mono truncate">
                <span className="text-purple-400">{selectedTrack.name}</span> · prompt 01/12
              </div>
              <div className="flex items-center gap-1 text-[10px] text-zinc-600 font-mono">
                <Copy size={10} />
                <span className="hidden sm:inline">copy</span>
              </div>
            </div>
            <div className="min-h-[180px] rounded-md border border-purple-500/20 bg-black/40 p-4">
              <div className="text-sm md:text-[15px] font-mono text-purple-100 leading-relaxed whitespace-pre-wrap break-words">
                {PROMPT_TEXT.slice(0, typedChars)}
                <span className="inline-block w-2 h-4 md:h-5 bg-purple-400 align-middle ml-0.5 animate-pulse" />
              </div>
            </div>
            <button
              disabled
              className={`mt-4 inline-flex items-center gap-2 rounded px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-[background-color,color,box-shadow] duration-500 ${
                typedChars >= PROMPT_TEXT.length
                  ? "bg-purple-500 text-black shadow-[0_0_20px_rgba(168,85,247,0.6)]"
                  : "bg-zinc-900 text-zinc-600"
              }`}
              data-testid="demo-run-button"
            >
              <Play size={12} />
              Run with AI
            </button>
          </div>

          {/* ── STAGE 2 · PROFIT ────────────────────────────── */}
          <div
            className={`transition-[opacity,transform] duration-500 ${
              stage === 2 || stage === 3
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-1 pointer-events-none absolute inset-x-4"
            }`}
            aria-hidden={stage !== 2 && stage !== 3}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono">
                <Sparkles size={12} className="text-purple-400" />
                <span>GPT-5.2 · streaming</span>
              </div>
              {stage === 3 && (
                <div className="flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-emerald-400 font-mono">
                  <Check size={12} />
                  Done
                </div>
              )}
            </div>
            <div className="max-h-[220px] min-h-[180px] overflow-hidden rounded-md border border-purple-500/20 bg-black/40 p-4">
              <div className="text-[13px] md:text-sm font-mono text-zinc-200 leading-relaxed whitespace-pre-wrap break-words">
                {streamedText}
                {stage === 2 && (
                  <span className="inline-block w-2 h-4 bg-purple-400 align-middle ml-0.5 animate-pulse" />
                )}
              </div>
            </div>
            {stage === 3 && (
              <div className="mt-4 flex items-center justify-between rounded-md border border-emerald-500/25 bg-emerald-500/5 px-4 py-3">
                <div className="flex items-center gap-2 text-emerald-300 text-xs font-mono tracking-wider">
                  <Check size={14} />
                  <span>Niche locked. Ship in 72h.</span>
                </div>
                <ArrowRight size={14} className="text-emerald-400/70" />
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes demoPulse {
          0%, 100% { box-shadow: 0 0 25px -5px rgba(168,85,247,0.5); }
          50% { box-shadow: 0 0 45px -5px rgba(168,85,247,0.85); }
        }
      `}</style>
    </div>
  );
}
